/* BRIZE Signature · bz-cart.js · [PANIER]
   Tiroir panier + page /cart : ouverture (bz:cart:open, bz:cart:add), quantités (− / + / champ), retrait,
   suggestion « Ajouter », note de commande, rendu par la Section Rendering API, barre de progression animée,
   messages d'erreur clairs, annonces aria-live, focus conservé après chaque rendu, glisser vers le bas (mobile).
   Émet bz:cart:changed + alias bz:cart:updated via Brize.cart.changed(cart) (contrat BUILD_CONTRACT §6.4).
   Aucune remise ni aucun pourcentage calculé ici : tout montant vient de Shopify (HTML rendu côté serveur).
   Module ES chargé après bz-theme.js. Les fonctions pures sont exportées pour les tests (Node). */

const NBSP = '\u00a0';

export const MESSAGES = Object.freeze({
  reseau: 'La connexion a été interrompue. Vérifiez votre réseau, puis réessayez.',
  quantite: "La quantité n'a pas pu être modifiée. Réessayez.",
  stock: "Il n'en reste pas assez pour cette quantité. Nous avons ajusté votre panier.",
  chargement: "Votre panier n'a pas pu être chargé. Rechargez la page.",
  ajout: "Cette taille n'a pas pu être ajoutée. Réessayez ou écrivez-nous.",
  ajoutProduit: "Cet article n'a pas pu être ajouté. Réessayez ou écrivez-nous.",
  general: "Une erreur s'est produite. Réessayez ou écrivez-nous.",
  ajoutee: 'Parure ajoutée au panier.',
  articleAjoute: 'Article ajouté au panier.',
  retiree: 'Parure retirée du panier.',
  articleRetire: 'Article retiré du panier.',
  qte: (n) => `Quantité mise à jour${NBSP}: ${n}.`,
  total: (t) => `Nouveau total${NBSP}: ${t}.`,
});

/* ---------- Fonctions pures ---------- */

export function entier(valeur, repli = 0) {
  const n = parseInt(String(valeur == null ? '' : valeur).trim(), 10);
  return Number.isFinite(n) ? n : repli;
}

/* Message clair selon l'erreur levée par Brize.fetchJSON (Error avec .status) ou par fetch (TypeError réseau). */
export function messageErreur(err, contexte = 'quantite') {
  if (err && err.name === 'AbortError') return '';
  if (!err || typeof err.status !== 'number') return MESSAGES.reseau;
  switch (contexte) {
    case 'ajout': return MESSAGES.ajout;
    case 'ajoutProduit': return MESSAGES.ajoutProduit;
    case 'chargement': return MESSAGES.chargement;
    case 'general': return MESSAGES.general;
    default: return MESSAGES.quantite;
  }
}

/* Une seule ligne -> /cart/change.js (id = clé de ligne) ; plusieurs -> /cart/update.js (updates par clé). */
export function construireModif(lot, ids = [], url = '/') {
  const extra = {};
  if (ids.length) { extra.sections = ids.slice(0, 5); extra.sections_url = url; }
  if (lot.size === 1) {
    const [[id, quantity]] = [...lot];
    return { route: 'cartChange', body: { id, quantity, ...extra } };
  }
  return { route: 'cartUpdate', body: { updates: Object.fromEntries(lot), ...extra } };
}

/* Lignes que Shopify a servies en moindre quantité que demandé (stock) */
export function lignesAjustees(cart, lot) {
  const out = [];
  if (!cart || !Array.isArray(cart.items)) return out;
  lot.forEach((demandee, key) => {
    if (demandee <= 0) return;
    const servie = cart.items.filter((i) => i.key === key).reduce((s, i) => s + i.quantity, 0);
    if (servie < demandee) out.push({ key, demandee, servie });
  });
  return out;
}

/* Champs d'un formulaire /cart/add -> article JSON { id, quantity, properties? } */
export function articleDepuisChamps(entries) {
  const article = { id: 0, quantity: 1 };
  const props = {};
  for (const [k, v] of entries) {
    if (k === 'id') article.id = entier(v);
    else if (k === 'quantity') article.quantity = Math.max(1, entier(v, 1));
    else {
      const m = /^properties\[(.+)\]$/.exec(k);
      if (m && String(v).trim() !== '') props[m[1]] = String(v);
    }
  }
  if (Object.keys(props).length) article.properties = props;
  return article;
}

/* Annonce lecteur d'écran après une modification réussie */
export function annonceModif({ lot, cart, parure = true, formatMoney = (c) => String(c) }) {
  const total = MESSAGES.total(formatMoney(cart.total_price));
  if (lot.size !== 1) return total;
  const [[key, q]] = [...lot];
  if (q === 0) return parure ? MESSAGES.retiree : MESSAGES.articleRetire;
  const n = cart.items.filter((i) => i.key === key).reduce((s, i) => s + i.quantity, 0);
  return `${MESSAGES.qte(n)} ${total}`;
}

export function sansSections(reponse) {
  if (!reponse || typeof reponse !== 'object') return reponse;
  const { sections, ...cart } = reponse; // eslint-disable-line no-unused-vars
  return cart;
}

/* File d'attente : regroupe les clics rapprochés (une requête par rafale) et sérialise toutes les requêtes. */
export class FileDAttente {
  constructor({ envoyer, delai = 350, planifier = (f, ms) => setTimeout(f, ms), annuler = (t) => clearTimeout(t) }) {
    this.envoyer = envoyer;
    this.delai = delai;
    this.planifier = planifier;
    this.annuler = annuler;
    this.attente = new Map();
    this.minuteur = null;
    this.chaine = Promise.resolve();
  }
  quantite(cle, q, { immediat = false } = {}) {
    this.attente.set(cle, Math.max(0, entier(q)));
    if (this.minuteur !== null) { this.annuler(this.minuteur); this.minuteur = null; }
    if (immediat) return this.vider();
    this.minuteur = this.planifier(() => { this.minuteur = null; this.vider(); }, this.delai);
    return undefined;
  }
  vider() {
    if (this.minuteur !== null) { this.annuler(this.minuteur); this.minuteur = null; }
    if (!this.attente.size) return this.chaine;
    const lot = new Map(this.attente);
    this.attente.clear();
    return this.tache(() => this.envoyer(lot));
  }
  tache(fn) {
    const p = this.chaine.then(() => fn());
    this.chaine = p.catch(() => {});
    return p;
  }
  enAttente() { return this.attente.size > 0 || this.minuteur !== null; }
}

/* Appels réseau (fetchJSON = Brize.fetchJSON, routes = BZ.routes) */
export function creerReseau({ fetchJSON, routes }) {
  return {
    modifier(lot, ids, url) {
      const { route, body } = construireModif(lot, ids, url);
      return fetchJSON(routes[route], { method: 'POST', body });
    },
    ajouter(articles, ids, url) {
      const body = { items: articles };
      if (ids.length) { body.sections = ids.slice(0, 5); body.sections_url = url; }
      return fetchJSON(routes.cartAdd, { method: 'POST', body });
    },
    note(texte) { return fetchJSON(routes.cartUpdate, { method: 'POST', body: { note: texte } }); },
    panier() { return fetchJSON(`${routes.cart}.js`); },
    sections(ids, url) {
      const sep = url.includes('?') ? '&' : '?';
      return fetchJSON(`${url}${sep}sections=${encodeURIComponent(ids.slice(0, 5).join(','))}`);
    },
  };
}

/* ---------- Interface (navigateur) ---------- */

const doc = typeof document !== 'undefined' ? document : null;
const echapper = (v) => (typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(v) : String(v).replace(/["\\]/g, '\\$&'));

function cssPret() {
  const link = doc.getElementById('bz-cart-css');
  if (!link || link.media === 'all') return Promise.resolve();
  return new Promise((resolve) => {
    let fait = false;
    const fini = () => { if (fait) return; fait = true; link.media = 'all'; resolve(); };
    if (link.sheet) { fini(); return; }
    link.addEventListener('load', fini, { once: true });
    link.addEventListener('error', fini, { once: true });
    setTimeout(fini, 2000);
  });
}

export class Panier {
  constructor(B) {
    this.B = B;
    this.reseau = creerReseau({ fetchJSON: B.fetchJSON, routes: B.config.routes });
    this.file = new FileDAttente({ envoyer: (lot) => this.envoyerModifs(lot) });
    this.synchro = Date.now();
    this.envoiAutorise = false;
    this.noteDiff = B.debounce((texte) => this.enregistrerNote(texte), 600);
    this.memoriser(doc);
    this.brancher();
  }

  get tiroir() { return doc.querySelector('[data-bz-cart-drawer]'); }
  racines() { return [...doc.querySelectorAll('[data-bz-cart][data-bz-section-id]')]; }
  ids() { return [...new Set(this.racines().map((r) => r.dataset.bzSectionId))]; }
  url() { return window.location.pathname; }

  memoriser(portee) {
    portee.querySelectorAll('[data-bz-cart-zone]').forEach((z) => { z._bzHtml = z.innerHTML; });
  }

  brancher() {
    const B = this.B;
    B.on('bz:cart:open', (e) => this.ouvrir(e.detail && e.detail.trigger));
    B.on('bz:cart:add', (e) => this.apresAjout(e.detail || {}));
    doc.addEventListener('click', (e) => this.clic(e));
    doc.addEventListener('change', (e) => this.changement(e));
    doc.addEventListener('keydown', (e) => this.touche(e));
    doc.addEventListener('submit', (e) => this.soumission(e));
    doc.addEventListener('input', (e) => {
      if (e.target.matches && e.target.matches('[data-bz-cart-note]')) this.noteDiff(e.target.value);
    });
    window.addEventListener('pageshow', (e) => { if (e.persisted) this.file.tache(() => this.rafraichir({ silencieux: true })); });
    /* Éditeur de thème */
    doc.addEventListener('shopify:section:load', (e) => this.memoriser(e.target));
    doc.addEventListener('shopify:section:unload', (e) => {
      const t = e.target.querySelector && e.target.querySelector('[data-bz-cart-drawer]');
      if (t && B.panel.isOpen(t)) B.panel.close(t);
    });
    doc.addEventListener('shopify:section:select', (e) => { if (e.detail && e.detail.sectionId === 'bz-cart-drawer') this.ouvrir(null, { sansSynchro: true }); });
    doc.addEventListener('shopify:section:deselect', (e) => {
      if (e.detail && e.detail.sectionId === 'bz-cart-drawer' && this.tiroir) B.panel.close(this.tiroir);
    });
    this.glisser();
  }

  /* ----- Ouverture ----- */
  async ouvrir(declencheur, { sansSynchro = false } = {}) {
    const t = this.tiroir;
    if (!t) {
      const page = doc.querySelector('[data-bz-cart="page"] [data-bz-focus-repli]');
      if (page) page.focus();
      else window.location.href = this.B.config.routes.cart;
      return;
    }
    if (this.B.panel.isOpen(t)) return;
    await cssPret();
    const titre = t.querySelector('[data-bz-focus-repli]');
    this.B.panel.open(t, { trigger: declencheur || doc.activeElement, initial: titre || undefined });
    if (!sansSynchro && Date.now() - this.synchro > 30000) this.file.tache(() => this.rafraichir({ silencieux: true }));
  }

  /* Ajout fait ailleurs ([PRODUIT] : fiche, barre collante, offre) : rafraîchir puis ouvrir */
  apresAjout(detail) {
    const declencheur = doc.activeElement;
    this.effacerErreurs();
    return this.file.tache(async () => {
      const ids = this.ids();
      const fournies = detail.sections && ids.length && ids.every((id) => typeof detail.sections[id] === 'string');
      if (fournies) {
        this.rendre(detail.sections);
        const cart = await this.reseau.panier().catch(() => null);
        if (cart) this.B.cart.changed(cart); else this.compteurDepuisDom();
      } else {
        const cart = await this.rafraichir({ silencieux: true });
        if (!cart) this.erreur(MESSAGES.chargement);
      }
      if (this.tiroir) await this.ouvrir(declencheur, { sansSynchro: true });
    });
  }

  /* ----- Événements ----- */
  clic(e) {
    if (!e.target.closest) return;
    const racine = e.target.closest('[data-bz-cart]');
    if (!racine) return;
    const btn = e.target.closest('[data-bz-qte-moins], [data-bz-qte-plus]');
    if (btn) {
      e.preventDefault();
      if (btn.getAttribute('aria-disabled') === 'true') return;
      this.pas(btn, btn.hasAttribute('data-bz-qte-plus') ? 1 : -1);
      return;
    }
    const retirer = e.target.closest('[data-bz-retirer]');
    if (retirer && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
      const ligne = retirer.closest('[data-bz-ligne]');
      if (!ligne) return;
      e.preventDefault();
      const input = ligne.querySelector('[data-bz-qte-input]');
      if (input) input.value = '0';
      this.modifier(ligne, 0, { immediat: true });
    }
  }

  pas(btn, delta) {
    const ligne = btn.closest('[data-bz-ligne]');
    const input = ligne && ligne.querySelector('[data-bz-qte-input]');
    if (!input) return;
    const actuelle = entier(input.value, entier(ligne.dataset.quantite, 1));
    const q = Math.max(1, actuelle + delta); /* « − » s'arrête à 1 : « Retirer » sert à enlever */
    if (q === actuelle) return;
    input.value = String(q);
    this.modifier(ligne, q);
  }

  changement(e) {
    const input = e.target.closest && e.target.closest('[data-bz-qte-input]');
    if (!input || !input.closest('[data-bz-cart]')) return;
    this.validerChamp(input);
  }

  validerChamp(input) {
    const ligne = input.closest('[data-bz-ligne]');
    if (!ligne) return;
    const q = entier(input.value, -1);
    if (q < 0) { input.value = ligne.dataset.quantite || '1'; return; }
    input.value = String(q);
    if (input.dataset.bzEnvoye === String(q)) return;
    if (String(q) === ligne.dataset.quantite && !this.file.attente.has(ligne.dataset.bzLigne)) return;
    input.dataset.bzEnvoye = String(q);
    this.modifier(ligne, q, { immediat: true });
  }

  touche(e) {
    if (e.key !== 'Enter') return;
    const input = e.target.closest && e.target.closest('[data-bz-qte-input]');
    if (!input || !input.closest('[data-bz-cart]')) return;
    e.preventDefault(); /* sinon Entrée enverrait le formulaire (paiement) */
    this.validerChamp(input);
  }

  soumission(e) {
    const form = e.target;
    if (!form.closest) return;
    /* Suggestion : ajout AJAX */
    if (form.matches('form[data-bz-cart-ajout]') && form.closest('[data-bz-cart]')) {
      e.preventDefault();
      this.ajouterSuggestion(form);
      return;
    }
    /* Paiement : envoyer d'abord les quantités encore en attente */
    if (form.matches('.bz-cart__form')) {
      if (this.envoiAutorise) { this.envoiAutorise = false; return; }
      if (!this.file.enAttente()) return;
      e.preventDefault();
      const sub = e.submitter;
      this.file.vider().then(() => this.file.chaine).then(() => {
        this.envoiAutorise = true;
        if (form.requestSubmit) form.requestSubmit(sub && sub.form === form ? sub : undefined); else form.submit();
      });
    }
  }

  /* ----- Modifications ----- */
  modifier(ligne, q, opts) {
    this.effacerErreurs();
    ligne.classList.add('bz-cart-ligne--maj');
    const moins = ligne.querySelector('[data-bz-qte-moins]');
    if (moins) { if (q <= 1) moins.setAttribute('aria-disabled', 'true'); else moins.removeAttribute('aria-disabled'); }
    this.occupe(true);
    this.file.quantite(ligne.dataset.bzLigne, q, opts);
  }

  async envoyerModifs(lot) {
    const B = this.B;
    let parure = true;
    if (lot.size === 1) {
      const [[cle]] = [...lot];
      const l = doc.querySelector(`[data-bz-ligne="${echapper(cle)}"]`);
      parure = !l || l.dataset.bzParure !== 'false';
    }
    let rep;
    try {
      rep = await this.reseau.modifier(lot, this.ids(), this.url());
    } catch (err) {
      const msg = messageErreur(err, 'quantite');
      if (this.file.enAttente()) return;
      if (err && err.status === 422) await this.rafraichir({ silencieux: true });
      else this.retablir();
      this.erreurLignes([...lot.keys()], msg);
      this.occupe(false);
      return;
    }
    this.synchro = Date.now();
    if (this.file.enAttente()) return; /* un envoi plus récent arrive : c'est lui qui rendra */
    if (rep && rep.sections) this.rendre(rep.sections);
    const cart = sansSections(rep);
    B.cart.changed(cart);
    this.occupe(false);
    const ajustees = lignesAjustees(cart, lot);
    if (ajustees.length) this.erreurLignes(ajustees.map((a) => a.key), MESSAGES.stock);
    else B.announce(annonceModif({ lot, cart, parure, formatMoney: (c) => B.formatMoney(c) }));
  }

  ajouterSuggestion(form) {
    if (form.dataset.bzEnvoi) return;
    form.dataset.bzEnvoi = '1';
    const B = this.B;
    const type = form.dataset.bzAjoutType || 'alternance';
    const bouton = form.querySelector('[type="submit"]');
    const zoneErreur = form.querySelector('[data-bz-ajout-erreur]');
    if (zoneErreur) { zoneErreur.hidden = true; zoneErreur.textContent = ''; }
    this.effacerErreurs();
    if (bouton) bouton.setAttribute('aria-busy', 'true');
    const article = articleDepuisChamps([...new FormData(form).entries()]);
    this.occupe(true);
    return this.file.tache(async () => {
      try {
        const rep = await this.reseau.ajouter([article], this.ids(), this.url());
        if (rep && rep.sections) this.rendre(rep.sections);
        else await this.rafraichir({ silencieux: true });
        const cart = await this.reseau.panier().catch(() => null);
        if (cart) B.cart.changed(cart); else this.compteurDepuisDom();
        B.announce(type === 'produit' ? MESSAGES.articleAjoute : MESSAGES.ajoutee);
      } catch (err) {
        const msg = messageErreur(err, type === 'produit' ? 'ajoutProduit' : 'ajout');
        if (form.isConnected && zoneErreur) { zoneErreur.textContent = msg; zoneErreur.hidden = false; } else this.erreur(msg);
      } finally {
        if (bouton) bouton.removeAttribute('aria-busy');
        delete form.dataset.bzEnvoi;
        this.occupe(false);
      }
    });
  }

  enregistrerNote(texte) {
    return this.file.tache(() => this.reseau.note(texte).catch((err) => this.erreur(messageErreur(err, 'general'))));
  }

  async rafraichir({ silencieux = false } = {}) {
    const ids = this.ids();
    if (!ids.length) return null;
    try {
      const [cart, sections] = await Promise.all([this.reseau.panier(), this.reseau.sections(ids, this.url())]);
      this.rendre(sections);
      this.B.cart.changed(cart);
      return cart;
    } catch (err) {
      if (!silencieux) this.erreur(MESSAGES.chargement);
      return null;
    }
  }

  /* ----- Rendu ----- */
  rendre(sections) {
    if (!sections) return;
    const parseur = new DOMParser();
    this.racines().forEach((racine) => {
      const id = racine.dataset.bzSectionId;
      const html = sections[id];
      if (typeof html !== 'string') return;
      const neuve = parseur.parseFromString(html, 'text/html')
        .querySelector(`[data-bz-cart][data-bz-section-id="${echapper(id)}"]`);
      if (!neuve) return;
      const zones = {};
      neuve.querySelectorAll('[data-bz-cart-zone]').forEach((z) => { zones[z.dataset.bzCartZone] = { html: z.innerHTML, hidden: z.hidden }; });
      this.remplacer(racine, {
        zones,
        nombre: neuve.dataset.bzCartNombre,
        vide: neuve.hasAttribute('data-bz-vide'),
      });
    });
    this.synchro = Date.now();
  }

  /* Remet l'état serveur précédent (après une erreur) */
  retablir() {
    this.racines().forEach((racine) => {
      const zones = {};
      racine.querySelectorAll('[data-bz-cart-zone]').forEach((z) => {
        if (typeof z._bzHtml === 'string') zones[z.dataset.bzCartZone] = { html: z._bzHtml, hidden: z.hidden };
      });
      this.remplacer(racine, { zones, force: true });
    });
  }

  remplacer(racine, { zones, nombre, vide, force = false }) {
    const actif = doc.activeElement;
    const focusDedans = !!(actif && actif !== doc.body && racine.contains(actif));
    const cleFocus = focusDedans ? actif.getAttribute('data-bz-focus') : null;
    const ligneFocus = focusDedans && actif.closest ? actif.closest('[data-bz-ligne]') : null;
    const indexLigne = ligneFocus ? [...racine.querySelectorAll('[data-bz-ligne]')].indexOf(ligneFocus) : -1;
    const note = racine.querySelector('[data-bz-cart-note]');
    const noteValeur = note ? note.value : null;
    const ouverts = new Map();
    racine.querySelectorAll('[data-bz-garder-ouvert]').forEach((d) => ouverts.set(d.dataset.bzGarderOuvert, d.open));
    const progres = racine.querySelector('[data-bz-progres]');
    const progresAvant = progres ? progres.dataset.bzProgres : null;

    racine.querySelectorAll('[data-bz-cart-zone]').forEach((zone) => {
      const n = zones[zone.dataset.bzCartZone];
      if (!n) return;
      zone.hidden = n.hidden;
      if (!force && zone._bzHtml === n.html) return;
      zone.innerHTML = n.html;
      zone._bzHtml = n.html;
    });
    /* Verrou légal de la précommande : le marqueur vit dans la zone « pied » (re-rendue), le bouton dans la zone fixe */
    const bloque = !!racine.querySelector('[data-bz-cart-bloque]');
    racine.querySelectorAll('[data-bz-paiement]').forEach((b) => { b.disabled = bloque; });
    racine.querySelectorAll('[data-bz-cart-express]').forEach((x) => { x.hidden = bloque; });
    if (nombre !== undefined) racine.dataset.bzCartNombre = nombre;
    if (vide !== undefined) racine.toggleAttribute('data-bz-vide', vide);
    racine.querySelectorAll('.bz-cart-ligne--maj').forEach((l) => l.classList.remove('bz-cart-ligne--maj'));

    /* Ce que la cliente avait en cours reste en place */
    const nouvelleNote = racine.querySelector('[data-bz-cart-note]');
    if (nouvelleNote && noteValeur !== null) nouvelleNote.value = noteValeur;
    racine.querySelectorAll('[data-bz-garder-ouvert]').forEach((d) => {
      const k = d.dataset.bzGarderOuvert;
      if (ouverts.has(k)) d.open = ouverts.get(k);
    });
    this.animerProgres(racine, progresAvant);

    if (focusDedans && !racine.contains(doc.activeElement)) {
      let cible = cleFocus ? racine.querySelector(`[data-bz-focus="${echapper(cleFocus)}"]`) : null;
      if (!cible && indexLigne >= 0) {
        const lignes = racine.querySelectorAll('[data-bz-ligne]');
        const l = lignes[Math.min(indexLigne, lignes.length - 1)];
        cible = l ? l.querySelector('[data-bz-qte-input]') : null;
      }
      if (!cible) cible = racine.querySelector('[data-bz-focus-repli]');
      if (cible) cible.focus({ preventScroll: true });
    }
    if (this.B.reveal && this.B.reveal.scan) this.B.reveal.scan(racine);
  }

  animerProgres(racine, avant) {
    const el = racine.querySelector('[data-bz-progres]');
    if (!el || avant === null || avant === undefined) return;
    const apres = el.dataset.bzProgres;
    if (avant === apres || this.B.motion.isReduced()) return;
    const lune = el.parentElement ? el.parentElement.querySelector('.bz-cart-progres__lune') : null;
    const cibles = [el, lune].filter(Boolean);
    cibles.forEach((x) => { x.style.transition = 'none'; x.style.setProperty('--bz-progres', avant); });
    el.getBoundingClientRect();
    requestAnimationFrame(() => {
      cibles.forEach((x) => { x.style.transition = ''; x.style.setProperty('--bz-progres', apres); });
    });
  }

  /* ----- États, erreurs ----- */
  occupe(oui) {
    this.racines().forEach((r) => { if (oui) r.setAttribute('aria-busy', 'true'); else r.removeAttribute('aria-busy'); });
  }

  erreur(message) {
    if (!message) return;
    const zones = this.racines().map((r) => r.querySelector('[data-bz-cart-erreur]')).filter(Boolean);
    if (!zones.length) { this.B.announce(message); return; }
    zones.forEach((p) => { p.textContent = message; p.hidden = false; });
  }

  erreurLignes(cles, message) {
    if (!message) return;
    let pose = false;
    cles.forEach((cle) => {
      doc.querySelectorAll(`[data-bz-cart] [data-bz-ligne="${echapper(cle)}"] [data-bz-ligne-erreur]`).forEach((p) => {
        p.textContent = message; p.hidden = false; pose = true;
      });
    });
    if (!pose) this.erreur(message);
  }

  effacerErreurs() {
    doc.querySelectorAll('[data-bz-cart] [data-bz-cart-erreur], [data-bz-cart] [data-bz-ligne-erreur]').forEach((p) => {
      if (!p.hidden) { p.hidden = true; p.textContent = ''; }
    });
  }

  compteurDepuisDom() {
    const r = this.racines()[0];
    if (r && r.dataset.bzCartNombre !== undefined) this.B.cart.setCount(entier(r.dataset.bzCartNombre));
  }

  /* ----- Feuille du bas (mobile) : glisser vers le bas pour fermer ----- */
  glisser() {
    const mq = window.matchMedia('(max-width: 767.98px)');
    let geste = null;
    doc.addEventListener('pointerdown', (e) => {
      const tete = e.target.closest && e.target.closest('[data-bz-cart-glisser]');
      if (!tete || !mq.matches || e.button !== 0 || e.target.closest('button, a, input')) return;
      const feuille = tete.closest('.bz-tiroir-panier__feuille');
      if (!feuille) return;
      geste = { y: e.clientY, t: performance.now(), feuille, id: e.pointerId, dy: 0 };
      if (tete.setPointerCapture) tete.setPointerCapture(e.pointerId);
      feuille.style.transition = 'none';
    });
    doc.addEventListener('pointermove', (e) => {
      if (!geste || e.pointerId !== geste.id) return;
      geste.dy = Math.max(0, e.clientY - geste.y);
      geste.feuille.style.transform = `translateY(${geste.dy}px)`;
    });
    const fin = (e) => {
      if (!geste || e.pointerId !== geste.id) return;
      const { feuille, dy, t } = geste;
      geste = null;
      const vitesse = dy / Math.max(1, performance.now() - t);
      feuille.style.transition = '';
      feuille.style.transform = '';
      if (dy > 120 || (dy > 40 && vitesse > 0.6)) this.B.panel.close(this.tiroir);
    };
    doc.addEventListener('pointerup', fin);
    doc.addEventListener('pointercancel', fin);
  }
}

/* ---------- Démarrage ---------- */
const Brize = typeof window !== 'undefined' ? window.Brize : undefined;
if (doc && Brize && !Brize.panier) {
  const demarrer = () => { if (!Brize.panier) Brize.panier = new Panier(Brize); };
  if (Brize.isReady) demarrer(); else Brize.ready(demarrer);
}

export default Panier;
